/**
 * ``{product_id: qty}`` for the signed-in customer's cart, kept in sync with every ``cart:update`` event, so a
 * page full of inline ADD / −qty+ controls shares one ``GET /cart``. Quantities of different variants of the same
 * product are summed, which is what a listing card shows.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { useAuth } from "../contexts/AuthContext";

/** The cart's lines (``[]`` when signed out / empty), refreshed on every ``cart:update``. */
export function useCartItems() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    useEffect(() => {
        let alive = true;
        const load = async () => {
            if (!user) { setItems([]); return; }
            try {
                const { data } = await api.get("/cart");
                if (alive) setItems(data?.items || []);
            } catch {
                if (alive) setItems([]);
            }
        };
        load();
        window.addEventListener("cart:update", load);
        return () => {
            alive = false;
            window.removeEventListener("cart:update", load);
        };
    }, [user]);
    return items;
}

export function useCartQuantities() {
    const items = useCartItems();
    return useMemo(() => {
        const m = {};
        for (const it of items) m[it.product_id] = (m[it.product_id] || 0) + (it.quantity || 0);
        return m;
    }, [items]);
}

/** Quantity of one exact cart line (product + variant; ``variantId`` null/undefined = the no-variant line). */
export function useCartLineQuantity(productId, variantId) {
    const items = useCartItems();
    const line = items.find((it) => it.product_id === productId && (it.variant_id || null) === (variantId || null));
    return line ? line.quantity || 0 : 0;
}
